/**
 * Dexie Sync Platform Adapter for Web
 *
 * Implements ISyncPlatformAdapter using Dexie (IndexedDB) for web platform.
 * Handles pending changes, sync metadata, network status, auth session, and background sync.
 */

import Dexie from 'dexie';
import { ISyncPlatformAdapter } from '../../interfaces/ISyncPlatformAdapter';
import { PendingChange } from '../../../../configs/tableConfig';
import { supabase } from '../../../../libs/supabaseClient';

/**
 * Dexie database schema for sync operations
 */
interface SyncDatabase extends Dexie {
  pending_changes: Dexie.Table<PendingChange, number>;
  sync_metadata: Dexie.Table<{ key: string; value: any }, string>;
}

export class DexieSyncAdapter implements ISyncPlatformAdapter {
  readonly platform: 'web' = 'web';

  private db: SyncDatabase;
  private networkListeners: Set<(online: boolean) => void> = new Set();
  private authUnsubscribe: (() => void) | null = null;

  constructor() {
    // Initialize Dexie database for sync operations
    this.db = new Dexie('SyncDB') as SyncDatabase;
    this.db.version(1).stores({
      pending_changes: '++id, table, action, record',
      sync_metadata: 'key, value'
    });

    // Set up network listener
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleNetworkChange);
      window.addEventListener('offline', this.handleNetworkChange);
    }

    // Set up auth state listener
    this.setupAuthListener();
  }

  // ==================== Network Status Handler ====================

  private handleNetworkChange = (): void => {
    const online = this.isOnline();
    this.networkListeners.forEach(callback => callback(online));
  };

  // ==================== Auth Listener Setup ====================

  private setupAuthListener(): void {
    try {
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (session) {
            this.setSyncMetadata('authSession', session);
          }
        }
        if (event === 'SIGNED_OUT') {
          this.setSyncMetadata('authSession', null);
        }
      });
      this.authUnsubscribe = data.subscription.unsubscribe;
    } catch (error) {
      console.warn('[DexieSyncAdapter] Could not set up auth listener:', error);
    }
  }

  // ==================== Pending Changes Operations ====================

  async getPendingChanges(): Promise<PendingChange[]> {
    return this.db.pending_changes.toArray();
  }

  async addPendingChange(change: Omit<PendingChange, 'id'>): Promise<number> {
    return this.db.pending_changes.add(change as PendingChange);
  }

  async removePendingChange(id: number): Promise<void> {
    await this.db.pending_changes.delete(id);
  }

  async removePendingChanges(ids: number[]): Promise<void> {
    if (ids.length === 0) return;
    await this.db.pending_changes.bulkDelete(ids);
  }

  async markPendingChange(id: number, error: string): Promise<void> {
    const existing = await this.db.pending_changes.get(id);
    if (existing) {
      await this.db.pending_changes.update(id, {
        lastError: error,
        retryCount: (existing.retryCount || 0) + 1
      });
    }
  }

  async getPendingChangesCount(): Promise<number> {
    return this.db.pending_changes.count();
  }

  async clearPendingChanges(): Promise<void> {
    await this.db.pending_changes.clear();
  }

  // ==================== Sync Metadata Operations ====================

  async getSyncMetadata(key: string): Promise<any> {
    const metadata = await this.db.sync_metadata.get(key);
    return metadata?.value;
  }

  async setSyncMetadata(key: string, value: any): Promise<void> {
    await this.db.sync_metadata.put({ key, value });
  }

  // ==================== Network Status Operations ====================

  isOnline(): boolean {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true; // Default to online in non-browser environments
  }

  onNetworkChange(callback: (online: boolean) => void): () => void {
    this.networkListeners.add(callback);
    // Return unsubscribe function
    return () => {
      this.networkListeners.delete(callback);
    };
  }

  // ==================== Auth Session Operations ====================

  async getAuthSession(): Promise<any> {
    return this.getSyncMetadata('authSession');
  }

  async setAuthSession(session: any): Promise<void> {
    await this.setSyncMetadata('authSession', session);
  }

  setupAuthStateListener(callback: (event: string, session: any) => void): void {
    try {
      const { data } = supabase.auth.onAuthStateChange(callback);
      // Store unsubscribe for cleanup if needed
      this.authUnsubscribe = data.subscription.unsubscribe;
    } catch (error) {
      console.warn('[DexieSyncAdapter] Could not set up auth state listener:', error);
    }
  }

  // ==================== Background Sync Operations ====================

  async registerBackgroundSync(): Promise<void> {
    if (typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('background-sync');
      } catch (error) {
        console.warn('[DexieSyncAdapter] Could not register background sync:', error);
      }
    }
  }

  // ==================== Cleanup ====================

  /**
   * Clean up resources (event listeners, subscriptions)
   */
  destroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleNetworkChange);
      window.removeEventListener('offline', this.handleNetworkChange);
    }

    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = null;
    }

    this.networkListeners.clear();
  }
}
